import { ColDef, ColumnState } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { getTabGridOptions } from './components/ColumnDefinitions';
import { defaultWMS2010_R01Dto, WMS2010_R01Dto } from './Dto_WMS2010_R01';
import useWMS2010_R01Store, { TabType } from './Store_WMS2010_R01';
import {
	calculateLogicCenterSubtotals,
	calculateWarehouseSubtotals,
	isSubtotalRow,
	mergeDataWithSubtotals,
} from './utils/subtotalUtils';
import { generateGridRowId, WMS2010_R01_TABS } from './utils/tabConfig';

interface StockGridBaseProps {
	gridRef: React.RefObject<AgGridReact<any> | null>;
	tabType: TabType;
	data: any[];
	columns: ColDef[];
	onTabChange: (tab: TabType) => Promise<void>;
}

const BOTTOM_LABEL_COLS = new Set(['itemCd', 'warehouseNm', 'logicCenterNm', 'lotNo']);

const normalizeHeaderClass = (defs: ColDef[]): ColDef[] =>
	defs.map((def: ColDef & { children?: ColDef[] }) => {
		const mergedHeaderClass = [aggridHeaderClass, def.headerClass].filter(Boolean).join(' ');
		const children = Array.isArray(def.children) && def.children.length > 0
			? normalizeHeaderClass(def.children)
			: undefined;
		return { ...def, headerClass: mergedHeaderClass, suppressMenu: true, children };
	});

const applyPinnedCenterAlign = (defs: ColDef[]): ColDef[] =>
	defs.map((def: ColDef & { colId?: string; cellStyle?: ColDef['cellStyle'] }) => {
		if (!def?.colId || !BOTTOM_LABEL_COLS.has(def.colId)) return def;
		const prevCellStyle = def.cellStyle;
		const cellStyle = (params: { node?: { rowPinned?: string | null } }) => {
			if (params?.node?.rowPinned === 'bottom') {
				return {
					...(typeof prevCellStyle === 'function' ? prevCellStyle(params as never) : prevCellStyle),
					textAlign: 'center',
				};
			}
			return typeof prevCellStyle === 'function' ? prevCellStyle(params as never) : prevCellStyle;
		};
		return { ...def, cellStyle };
	});

export const Table_WMS2010_R01 = ({
	gridRef,
	tabType,
	data,
	columns,
	onTabChange,
}: StockGridBaseProps) => {
	const {
		activeTab,
		tabColumnStates,
		setTabColumnState,
		tabSelectedIds,
		setTabSelectedId,
		showSubtotals,
		setTabSubtotalData,
		tabGridData,
		setTabGridData,
		tabScrollTop,
		setTabScrollTop,
	} = useWMS2010_R01Store();

	const [gridKey, setGridKey] = useState(0);
	const [, setHoveredRowIndex] = useState<number | null>(null);

	useEffect(() => { setGridKey((prev) => prev + 1); }, [tabType]);

	const subtotals = useMemo(() => {
		if (!data || data.length === 0 || tabType === 'item' || tabType === 'total' || tabType === 'lot') {
			return [];
		}
		switch (tabType) {
			case 'warehouse':
				return calculateWarehouseSubtotals(data);
			case 'logiccenter':
				return calculateLogicCenterSubtotals(data);
			default:
				return [];
		}
	}, [data, tabType]);

	const gridData = useMemo(() => {
		if (!data || data.length === 0) return [];
		return mergeDataWithSubtotals(data, subtotals, showSubtotals, tabType);
	}, [data, subtotals, showSubtotals, tabType]);

	useEffect(() => {
		setTabSubtotalData(tabType, subtotals);
		setTabGridData(tabType, gridData);
	}, [subtotals, gridData, tabType, setTabSubtotalData, setTabGridData]);

	const localStorageKey = useMemo(
		() => `WMS2010_R01_${tabType}_columns`
	, [tabType]);

	const activeTabGridData = tabGridData[tabType] || [];

	const handleTabClick = useCallback(async (tab: TabType) => {
		if (tab === activeTab) return;
		await onTabChange(tab);
	}, [onTabChange, activeTab]);

	const getRowId = useCallback((params: any) =>
		generateGridRowId(params.data, tabType)
	, [tabType]);

	const makeRowId = useCallback((data: any) =>
		generateGridRowId(data, tabType)
	, [tabType]);

	const baseColumnDefs = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		...columns,
	], [columns]);

	const effectiveColumnState = useMemo(() => {
		const savedState = tabColumnStates[tabType];
		return Array.isArray(savedState) && savedState.length === 0 ? null : savedState;
	}, [tabColumnStates, tabType]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs = useMemo(() => {
		const mergedColumnDefs = effectiveColumnState === null
			? baseColumnDefs
			: GridCustomColumnDefs({
				baseColumnDefs,
				columnState: effectiveColumnState,
				headerNames: {},
				fallbackHeaderClass: aggridHeaderClass,
			});
		return applyPinnedCenterAlign(normalizeHeaderClass(mergedColumnDefs));
	}, [baseColumnDefs, effectiveColumnState]);

	const localDefaultColDef = useMemo(() => ({
		...defaultColDef,
		suppressMenu: true,
		headerClass: aggridHeaderClass,
	}), []);

	const rowClassRules = useMemo(() => {
		const selectedIds = tabSelectedIds[tabType] || [];
		return {
			selected: (params: any) => {
				if (params.node.rowPinned === 'bottom') return false;
				if (!selectedIds.length) return false;
				const id = makeRowId(params.data);
				return selectedIds.includes(id);
			},
			hover: (params: any) => {
				if (params.node.rowPinned === 'bottom') return false;
				if (!params.data) return false;
				const id = makeRowId(params.data);
				return !selectedIds.includes(id);
			},
			'subtotal-row': (params: any) => isSubtotalRow(params.data),
		} as Record<string, (p: any) => boolean>;
	}, [tabSelectedIds, tabType, makeRowId]);

	const shiftRangeSelectionConfig = useMemo(() => ({
		enableShiftRangeSelect: true,
		selectedIds: tabSelectedIds[tabType] || [],
		setSelectedIds: (ids: Array<string | number>) => setTabSelectedId(tabType, ids.map(String)),
		resolveId: (row: WMS2010_R01Dto) => makeRowId(row),
		isRowSelectable: (event: any) => !isSubtotalRow(event.data),
	}), [tabSelectedIds, tabType, setTabSelectedId, makeRowId]);

	const pinnedBottomRowData = useMemo(() => {
		if (!Array.isArray(data) || data.length === 0) return [] as Record<string, unknown>[];

		const number = (v: unknown) => Number(v) || 0;
		const dataLength = data.length;

		switch (tabType) {
			case 'item':
				return [{
					itemCd: `${dataLength.toLocaleString()}건`,
					stockQty: data.reduce((s, r) => s + number(r.stockQty), 0),
					reservQty: data.reduce((s, r) => s + number(r.reservQty), 0),
					availableQty: data.reduce((s, r) => s + number(r.stockQty), 0),
				}];
			case 'warehouse':
				return [{
					warehouseNm: `${dataLength.toLocaleString()}건`,
					stockQty: data.reduce((s, r) => s + number(r.stockQty), 0),
					reservQty: data.reduce((s, r) => s + number(r.reservQty), 0),
					availableQty: data.reduce((s, r) => s + number(r.stockQty), 0),
				}];
			case 'logiccenter':
				return [{
					logicCenterNm: `${dataLength.toLocaleString()}건`,
					stockQty: data.reduce((s, r) => s + number(r.stockQty), 0),
					reservQty: data.reduce((s, r) => s + number(r.reservQty), 0),
					availableQty: data.reduce((s, r) => s + number(r.stockQty), 0),
				}];
			case 'lot':
				return [{
					lotNo: `${dataLength.toLocaleString()}건`,
					stockQty: data.reduce((s, r) => s + number(r.stockQty), 0),
					reservQty: data.reduce((s, r) => s + number(r.reservQty), 0),
					availableQty: data.reduce((s, r) => s + number(r.stockQty), 0),
				}];
			case 'total':
				return [{
					logicCenterNm: `${dataLength.toLocaleString()}건`,
					stockQty: data.reduce((s, r) => s + number(r.stockQty), 0),
					reservQty: data.reduce((s, r) => s + number(r.reservQty), 0),
					availableQty: data.reduce((s, r) => s + number(r.stockQty), 0),
				}];
			default:
				return [];
		}
	}, [data, tabType]);

	const handleSetColumnState = useCallback((state: ColumnState[] | null) => {
		setTabColumnState(tabType, state || []);
	}, [setTabColumnState, tabType]);

	return (
		<div className="w-full h-full flex flex-col">
			<div
				className="flex items-end mb-[0.1px]  bg-white h-[25px] "
				role="tablist"
				aria-label="재고 조회 탭"
			>
				{WMS2010_R01_TABS.map((tab) => {
					const isActive = activeTab === (tab.id as TabType);
					return (
						<button
							key={tab.id}
							onClick={() => handleTabClick(tab.id as TabType)}
							role="tab"
							aria-selected={isActive}
							className={`h-[26px] px-3 text-[12px] font-medium transition-colors border border-gray-300 border-b-0
								${isActive
									? 'bg-white text-black-700 -mb-px shadow-sm'
									: 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
							`}
						>
							{tab.label}
						</button>
					);
				})}
			</div>

			<div className="flex-1">
				<GridContextWrapper<WMS2010_R01Dto>
					enableCellCalculation
					gridRef={gridRef}
					paddedGridData={activeTabGridData}
					initSelected={() => {}}
					defaultRowData={defaultWMS2010_R01Dto}
					baseColumnDefs={effectiveColumnState ? columnDefs : baseColumnDefs}
					defaultColumnState={defaultColumnState}
					setColumnState={handleSetColumnState}
					localStorageKey={localStorageKey}
					menuItems={undefined}
				>
					<Grid_Touch<WMS2010_R01Dto>
						key={gridKey}
						ref={gridRef as React.RefObject<AgGridReact<WMS2010_R01Dto>>}
						rowData={activeTabGridData}
						columnDefs={columnDefs}
						defaultColDef={localDefaultColDef}
						getRowId={getRowId}
						rowClassRules={rowClassRules}
						onCellKeyDown={undefined}
						pinnedBottomRowData={pinnedBottomRowData}
						shiftRangeSelection={shiftRangeSelectionConfig}
						setHoveredRowIndex={setHoveredRowIndex}
						columnStateKey={localStorageKey}
						setColumnState={handleSetColumnState}
						scrollTop={tabScrollTop[tabType]}
						setScrollTop={(top) => setTabScrollTop(tabType, top)}
						isRowSelectable={(node) => !!node.data && !isSubtotalRow(node.data)}
						{...getTabGridOptions()}
					/>
				</GridContextWrapper>
			</div>
		</div>
	);
};
